import { cn } from "@/modules/lib/utils"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { getFormattedCouponReduction } from "@modules/common/couponObject/utils/couponObjectUtils"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { Percent, Tag } from "lucide-react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramSubscriberDetailsContentOnOffPriceProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberDetailsContentOnOffPrice({
  subscriberDetails,
  className,
  ...props
}: ProgramSubscriberDetailsContentOnOffPriceProps) {
  const language = useLanguage()

  return (
    <div className={cn("flex w-full flex-col gap-2", className)} {...props}>
      <div className="flex w-full items-baseline gap-2">
        <Text fontStyle="BS" className="text-textDefault-caption">
          {word("program.subscribers.subscriptionDetails.price.label")}
        </Text>
        <div className="flex grow border-t border-borderDefault" />
      </div>
      {subscriberDetails.paidPrice && !subscriberDetails.trial ? (
        <div className="flex flex-row items-center gap-2 py-1">
          <div className="flex flex-row items-baseline gap-1">
            <Text fontStyle="LL" className="text-textDefault-heading">
              {formatCurrency(subscriberDetails.paidPrice.value, subscriberDetails.paidPrice.currency, language)}
            </Text>
            {subscriberDetails.referencePrice &&
            subscriberDetails.nextPayment &&
            subscriberDetails.referencePrice.value !== subscriberDetails.nextPayment.value ? (
              <Text fontStyle="LM" className="text-textDefault-caption line-through">
                {formatCurrency(
                  subscriberDetails.referencePrice.value,
                  subscriberDetails.referencePrice.currency,
                  language,
                )}
              </Text>
            ) : null}
          </div>
        </div>
      ) : (
        <Text fontStyle="LL" className="text-textDefault-heading">
          {word("program.price.free")}
        </Text>
      )}

      <div className="flex w-full flex-col gap-3">
        {/* PROMO CODE */}
        {subscriberDetails.usedPromoCode && subscriberDetails.paidPrice ? (
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-center gap-1 pl-3">
              <Percent size={15} className="fill-textDefault-caption" />
              <Text fontStyle="LS" className="text-textDefault-caption">
                {word("box.member.table.offer.item.codePromo")}
              </Text>
            </div>
            <div className="flex flex-row items-baseline gap-1 pl-3">
              <Text fontStyle="LM" className="text-textDefault-heading">
                {subscriberDetails.usedPromoCode.code}
              </Text>
              <Text fontStyle="LS" className="text-textDefault-caption">
                -
                {getFormattedCouponReduction(
                  subscriberDetails.usedPromoCode.type,
                  subscriberDetails.usedPromoCode.value,
                  language,
                  subscriberDetails.paidPrice.currency,
                )}
              </Text>
            </div>
            <div className="flex flex-row items-baseline gap-1 pl-3">
              <Text fontStyle="LS" className="text-textDefault-caption">
                {`(${subscriberDetails.usedPromoCode.getWordingDate()})`}
              </Text>
            </div>
          </div>
        ) : null}

        {/* DISCOUNT */}
        {subscriberDetails.coupon && subscriberDetails.nextPayment ? (
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-center gap-1 pl-3">
              <Tag size={15} className="fill-textDefault-caption" />
              <Text fontStyle="LS" className="font-semibold text-textDefault-caption">
                {word("box.member.table.offer.item.discount.label")}
              </Text>
            </div>
            <div className="flex flex-col gap-1 pl-3">
              <Text fontStyle="BS" className="text-textDefault-heading">
                -{" "}
                {getFormattedCouponReduction(
                  subscriberDetails.coupon.type,
                  subscriberDetails.coupon.value,
                  language,
                  subscriberDetails.nextPayment.currency,
                )}
                {subscriberDetails.coupon.type === CouponType.PERCENT
                  ? ` = -${subscriberDetails.getWordingReductionForDiscount(language)}`
                  : null}
              </Text>
              <Text fontStyle="LS" className="text-textDefault-caption">
                {subscriberDetails.getWordingCouponDate()}
              </Text>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
